import { useQuery } from '@tanstack/react-query';
import ms from 'ms';
import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { ReactSelectInput } from '@hivebrite/design-system/Inputs';

import { useCurrentNetwork } from 'hb-react/shared/contexts';
import { axiosWithSyntaxHandlers } from 'hb-react/shared/utils/axios';

const ReactSelectTopics = ({
  id,
  name,
  ...props
}: ComponentProps<typeof ReactSelectInput>) => {
  const {
    currentNetwork: { id: networkId },
  } = useCurrentNetwork();

  const [inputValue, setInputValue] = useState('');

  // Extract topic IDs from props.value for fetching missing topics
  const topicIdsToFetch = useMemo(() => {
    if (!props.value) {
      return [];
    }

    const values = Array.isArray(props.value) ? props.value : [props.value];

    return values.map((value) =>
      typeof value === 'object' ? value.value : value,
    );
  }, [props.value]);

  // Single API call that handles both search and selected topic IDs
  const { data: topics = [], isLoading } = useQuery({
    queryKey: ['topics', networkId, inputValue, topicIdsToFetch],
    queryFn: async () => {
      const params: any = {
        format: 'json',
        per_page: 25,
      };

      // Add search parameter if there's input
      if (inputValue) {
        params.fulltext = inputValue;
      }

      // Add selected topic IDs if we have them and no search input
      if (topicIdsToFetch.length > 0 && !inputValue) {
        params.selected_topic_ids = topicIdsToFetch;
      }

      const response = await axiosWithSyntaxHandlers.get(
        Routes.backoffice_network_topics_path(networkId),
        { params },
      );

      return response.data;
    },
    staleTime: ms('5m'),
  });

  const onInputChange = useDebouncedCallback((value) => {
    setInputValue(value);
  }, 500);

  const options = useMemo(() => {
    const uniqueTopicsMap = new Map();

    // Add all fetched topics
    topics.forEach((topic) => {
      if (topic && typeof topic.id !== 'undefined') {
        uniqueTopicsMap.set(topic.id, topic);
      }
    });

    return Array.from(uniqueTopicsMap.values()).map(({ id, name }) => ({
      value: id,
      label: name,
    }));
  }, [topics]);

  return (
    <ReactSelectInput
      placeholder={__('Search and select groups')}
      isMulti
      {...props}
      cacheOptions
      id={id}
      name={name}
      options={options}
      isLoading={isLoading}
      // @ts-expect-error react-select is not typed
      onInputChange={onInputChange}
    />
  );
};

export default ReactSelectTopics;
